import React, { useState } from "react";
import {
  Book,
  MoreVertical,
  Calendar,
  TrendingUp,
  TrendingDown,
  Edit2,
  Trash2,
  Eye,
  BarChart3,
  Tag,
} from "lucide-react";

const LedgerCard = ({ ledger, onClick, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const ledgerData = ledger || {};
  const {
    _id = "",
    name = "Unnamed Book",
    description = "",
    createdAt = new Date().toISOString(),
    totalIncome = 0,
    totalExpense = 0,
    balance = 0,
    transactionCount = 0,
    category = "General",
    status = "active",
  } = ledgerData;

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    archived: "bg-gray-100 text-gray-700 border-gray-200",
    draft: "bg-amber-100 text-amber-700 border-amber-200",
  };

  const categoryColors = {
    Personal: "from-blue-500 to-cyan-500",
    Business: "from-purple-500 to-pink-500",
    Household: "from-emerald-500 to-teal-500",
    Travel: "from-orange-500 to-red-500",
    Project: "from-violet-500 to-purple-500",
    General: "from-gray-600 to-gray-700",
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "Recently";
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `₹${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount}`;
  };

  const getCategoryClass = () => {
    return categoryColors[category] || categoryColors.General;
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onEdit) onEdit(ledger);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDelete) {
      onDelete(ledger);
    }
  };
  const handleView = (e) => {
    e.stopPropagation();
    if (onClick) onClick(ledger);
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group"
    >
      <div className="bg-white rounded-xl border border-gray-200 hover:border-indigo-300 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-lg">
        {/* Header with gradient */}
        <div className={`relative h-3 bg-gradient-to-r ${getCategoryClass()}`}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
        </div>

        {/* Card Content */}
        <div className="p-5">
          {/* Top Row - Title & Menu */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    categoryColors[category]
                      ?.split(" ")[0]
                      .replace("from-", "bg-") + "-100"
                  }`}
                >
                  <Book
                    size={16}
                    className={
                      categoryColors[category]
                        ?.split(" ")[0]
                        .replace("from-", "text-") + "-600"
                    }
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 truncate">
                    {name}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[status]}`}
                    >
                      {status}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Tag size={10} />
                      {category}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <button
                onClick={handleMoreClick}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <MoreVertical size={18} className="text-gray-500" />
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                    }}
                  />
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 animate-in fade-in slide-in-from-top-2">
                    <button
                      onClick={handleView}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Eye size={16} />
                      View Details
                    </button>
                    <button
                      onClick={handleEdit}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Edit2 size={16} />
                      Edit Book
                    </button>
                    <button
                      onClick={handleDelete}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      Delete Book
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          {description && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
              {description}
            </p>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-50 p-3 rounded-lg flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={14} className="text-emerald-600" />
                <span className="text-xs font-medium text-gray-600">
                  Income
                </span>
              </div>
              <p className="text-lg font-bold text-emerald-700">
                {formatCurrency(totalIncome)}
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown size={14} className="text-rose-600" />
                <span className="text-xs font-medium text-gray-600">
                  Expense
                </span>
              </div>
              <p className="text-lg font-bold text-rose-700">
                {formatCurrency(totalExpense)}
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 size={14} className="text-indigo-600" />
                <span className="text-xs font-medium text-gray-600">
                  Balance
                </span>
              </div>
              <p
                className={`text-lg font-bold ${
                  balance >= 0 ? "text-emerald-700" : "text-rose-700"
                }`}
              >
                {formatCurrency(Math.abs(balance))}
              </p>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <Calendar size={14} className="text-blue-600" />
                <span className="text-xs font-medium text-gray-600">
                  Transactions
                </span>
              </div>
              <p className="text-lg font-bold text-blue-700">
                {transactionCount}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Calendar size={12} />
              <span>{formatDate(createdAt)}</span>
            </div>
            <button
              onClick={handleView}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-full transition-all duration-150"
            >
              Open
            </button>
          </div>

          {/* Hover effect border */}
          {isHovered && (
            <div className="absolute inset-0 border-2 border-indigo-400 rounded-xl pointer-events-none"></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LedgerCard;
